import { useState } from 'react';
import { useQuery } from '@apollo/client';
import { Download, HardDrive } from 'lucide-react';
import { Button } from '../ui/button';
import { Card } from '../ui/card';
import { GET_COLLECTION_USAGE } from '../../graphql';
import { useAccount } from '../../context/AccountContext';

/**
 * What the collection is costing, and how to take it away.
 *
 * The export is here rather than buried in a menu on purpose. If a subscription
 * lapses the public site goes dark and capture stops, but the catalogue stays
 * readable and downloadable — so the button someone needs on their worst day
 * with us should be somewhere they have already seen it.
 */

const backendHost = process.env.REACT_APP_BACKEND_HOST || 'http://localhost:4000';

function formatBytes(bytes: number): string {
  if (!bytes) return '0 MB';
  const mb = bytes / (1024 * 1024);
  if (mb < 1024) return `${mb.toFixed(mb < 10 ? 1 : 0)} MB`;
  return `${(mb / 1024).toFixed(2)} GB`;
}

export function CollectionDataSettings() {
  const { accountId, isLoading: accountsLoading } = useAccount();
  const [downloading, setDownloading] = useState<string | null>(null);
  const [error, setError] = useState('');

  const { data, loading } = useQuery(GET_COLLECTION_USAGE, {
    variables: { accountId },
    skip: !accountId,
    fetchPolicy: 'cache-and-network',
  });

  const usage = data?.collectionUsage;

  /**
   * Fetched rather than linked, because the export endpoint authenticates with
   * the session JWT and a plain <a href> cannot carry an Authorization header.
   */
  const download = async (format: 'csv' | 'json') => {
    if (!accountId) return;
    setError('');
    setDownloading(format);
    try {
      const params = new URLSearchParams({ accountId, format });
      const res = await fetch(`${backendHost}/api/collect/export?${params}`, {
        headers: { authorization: `Bearer ${localStorage.getItem('token') ?? ''}` },
      });
      if (!res.ok) {
        const body = await res.json().catch(() => ({}));
        throw new Error(body?.error || `Export failed (${res.status})`);
      }
      const blob = await res.blob();
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `collection-${new Date().toISOString().slice(0, 10)}.${format}`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(url);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Export failed');
    } finally {
      setDownloading(null);
    }
  };

  if (accountsLoading) return <div className="p-6 text-cmp-ink-2">Loading…</div>;

  return (
    <div className="space-y-4 max-w-2xl">
      <div>
        <h2 className="text-lg font-semibold text-cmp-ink flex items-center gap-2">
          <HardDrive className="w-5 h-5" /> Your data
        </h2>
        <p className="text-sm text-cmp-ink-2 mt-1">
          What your collection is using, and a copy of all of it whenever you want one.
        </p>
      </div>

      <Card className="p-4 space-y-4 bg-cmp-raise border border-cmp-line text-cmp-ink">
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-4">
          {[
            { label: 'Items', value: loading && !usage ? '—' : String(usage?.items ?? 0) },
            { label: 'Published', value: loading && !usage ? '—' : String(usage?.published ?? 0) },
            { label: 'Photos', value: loading && !usage ? '—' : String(usage?.assets ?? 0) },
            {
              label: 'Storage',
              value: loading && !usage ? '—' : formatBytes(usage?.storageBytes ?? 0),
            },
          ].map((stat) => (
            <div key={stat.label}>
              <div className="text-xl font-semibold text-cmp-ink tabular-nums">{stat.value}</div>
              <div className="text-xs text-cmp-ink-3">{stat.label}</div>
            </div>
          ))}
        </div>

        <div className="pt-3 border-t border-cmp-line space-y-2">
          <div className="text-sm font-medium">Export</div>
          <p className="text-xs text-cmp-ink-2">
            Every item with its fields, taxonomy terms, listing data and image links. CSV opens in a
            spreadsheet; JSON keeps the full structure. This stays available even if your
            subscription lapses — your catalogue is yours.
          </p>
          <div className="flex items-center gap-2 flex-wrap">
            <Button
              variant="outline"
              size="sm"
              disabled={downloading !== null}
              onClick={() => void download('csv')}
            >
              <Download className="w-4 h-4" />
              <span className="ml-2">{downloading === 'csv' ? 'Preparing…' : 'CSV'}</span>
            </Button>
            <Button
              variant="outline"
              size="sm"
              disabled={downloading !== null}
              onClick={() => void download('json')}
            >
              <Download className="w-4 h-4" />
              <span className="ml-2">{downloading === 'json' ? 'Preparing…' : 'JSON'}</span>
            </Button>
            {error && <span className="text-sm text-red-400">{error}</span>}
          </div>
        </div>
      </Card>
    </div>
  );
}
